import { NetworkMajorVersion } from '@block-explorer/entities';
import i18n from '../i18n.ts';

interface Messages {
  common: Record<'deploys' | 'deploy' | 'hash' | 'page', string>;
  associatedKeys: Record<
    | 'submission'
    | 'submissionThresholds'
    | 'submissionThresholdsErrorTitle'
    | 'submissionError'
    | 'associatedKeysRemoval'
    | 'tooltip',
    string
  >;
  homeScene: Record<
    | 'past14days'
    | 'total'
    | 'latestDeploys'
    | 'hash'
    | 'viewAll'
    | 'chartDeploysNumber',
    string
  >;
  deployDetails: Record<
    | 'pageTitle'
    | 'tooltip'
    | 'expireIn'
    | 'actionTooltip'
    | 'resultTooltip'
    | 'amountTooltip'
    | 'paymentAmountTooltip'
    | 'consumedGasTooltip'
    | 'chargedAmountTooltip',
    string
  >;
  contracts: Record<'tooltip', string>;
  faucet: Record<'requests', string>;
  analytics: Record<'deploysOverTime' | 'total' | 'graphKey', string>;
  associatedKeysLearnMore: Record<
    'keyManagement' | 'weightInfo' | 'thresholdInfo' | 'video',
    string
  >;
  account: Record<'transfers', string>;
  transfer: Record<'error' | 'hash', string>;
  multisig: Record<
    | 'submitSignedTransactionFieldName'
    | 'tooltip'
    | 'created'
    | 'submitted'
    | 'deployProcessed'
    | 'copyLink'
    | 'expire'
    | 'cantSign'
    | 'alreadySigned'
    | 'hasSignature'
    | 'notEnoughAlert'
    | 'expireAt'
    | 'accountBalance',
    any
  >;
}

export const v2Messages: Record<NetworkMajorVersion, Messages> = {
  [NetworkMajorVersion.V1]: {
    common: {
      deploys: i18n.t('Deploys'),
      deploy: i18n.t('Deploy'),
      hash: i18n.t('Deploy Hash'),
      page: i18n.t('Deploy detail page'),
    },
    associatedKeys: {
      submission: i18n.t('Deploy submission'),
      submissionThresholds: i18n.t('Deploy submission threshold'),
      submissionThresholdsErrorTitle: i18n.t(
        'Deploy submission threshold error'
      ),
      submissionError: i18n.t(
        'The deploy submission threshold cannot be greater than the key management threshold'
      ),
      associatedKeysRemoval: i18n.t(
        "You are removing your key from the associated keys. It will lose it's signing rights after the deploy is executed."
      ),
      tooltip: i18n.t('Signature weight required to submit a deploy'),
    },
    homeScene: {
      past14days: i18n.t('DEPLOYS IN PAST 14 DAYS'),
      total: i18n.t('TOTAL DEPLOYS'),
      latestDeploys: i18n.t('Latest Deploys'),
      hash: i18n.t('Deploy Hash'),
      viewAll: i18n.t('View all Deploys'),
      chartDeploysNumber: i18n.t('Number of deploys'),
    },
    deployDetails: {
      pageTitle: i18n.t('Deploy Details'),
      tooltip: i18n.t('Deploy Details'),
      expireIn: i18n.t(
        "Deploy is waiting in the queue for the limited time. If the queue is too long and the deploy is not executed, the status will change to 'Expired' and the deploy will need to be done again. You can count expiration date as Timestamp + Expires in. The account is not charged for expired deploys."
      ),
      actionTooltip: i18n.t('Action the deploy caller intended to perform'),
      resultTooltip: i18n.t(
        'Changes to the blockchain state this deploy resulted in'
      ),
      amountTooltip: i18n.t('Amount that is the subject of the deploy.'),
      paymentAmountTooltip: i18n.t(
        'Payment amount specifies the maximum amount that can be charged for executing the deploy. Part of the unused payment amount is refunded.'
      ),
      consumedGasTooltip: i18n.t(
        'The gas value, in CSPR, consumed to execute the deploy.'
      ),
      chargedAmountTooltip: i18n.t(
        'CSPR tokens spent for executing the deploy. Calculated as the payment amount minus the partial refund of unused gas.'
      ),
    },
    contracts: {
      tooltip: i18n.t(
        'Number of deploys that call the contract for the rest of the contracts'
      ),
    },
    faucet: {
      requests: i18n.t('deploy requests'),
    },
    analytics: {
      deploysOverTime: i18n.t('Daily deploys over time'),
      total: i18n.t('Total deploys'),
      graphKey: 'total_deploys',
    },
    associatedKeysLearnMore: {
      keyManagement: i18n.t(
        'The key management threshold specifies the weight required to modify\n' +
          '      the associated key settings, which is done via deploy that calls a\n' +
          '      corresponding smart contract.'
      ),

      weightInfo: i18n.t(
        'An associated key is an account with a weight assigned to\n' +
          '      it. Accounts listed as associated keys can sign and submit\n' +
          '      deploys on behalf of the master account as long as the\n' +
          '      cumulative weight of their signatures meets or exceeds the\n' +
          '      threshold required for the performed operation.'
      ),

      thresholdInfo: i18n.t(
        'The deploy submission threshold specifies the weight required to\n' +
          '      submit any other kind of a deploy.'
      ),

      video: i18n.t(
        '10 minutes tutorial on how to set up associated keys and multisig deploys.'
      ),
    },
    account: {
      transfers: i18n.t('Transfers'),
    },
    transfer: {
      error: i18n.t('Deploy was not found'),
      hash: i18n.t('The Deploy Hash for your transaction'),
    },
    multisig: {
      submitSignedTransactionFieldName: 'deploy',
      tooltip: i18n.t(
        'Casper network allows accounts to authorise other accounts to\n' +
          '              submit deploys on their behalf. To do that, the authorised\n' +
          '              accounts need to collect enough signatures to satisfy the\n' +
          '              requirements imposed by the master account.'
      ),
      created: i18n.t('Deploy created'),
      submitted: i18n.t('Deploy submitted'),
      deployProcessed: i18n.t(
        'Your associated key settings will update once the deploy is\n' +
          '      processed. It typically takes less than 1 minute, but the actual\n' +
          '      time depends on the level of the network activity. You can check\n' +
          '      the deploy status by clicking on'
      ),
      copyLink: i18n.t(
        ' Please copy this link and share it with the other parties to\n' +
          '      add their signatures. The deploy will be automatically\n' +
          '      submitted, once enough weight is obtained.'
      ),
      expire: i18n.t('Deploy expires in 2 hours.'),
      cantSign: i18n.t('You can’t sign this deploy'),
      alreadySigned: i18n.t("You've already signed this deploy"),
      hasSignature: i18n.t(
        'The deploy you are attempting to sign already contains your signature.'
      ),
      notEnoughAlert: {
        title: i18n.t(
          'Your signature will not add enough weight to submit this deploy to the network.'
        ),
        description: i18n.t(
          'Once you sign it, you’ll be presented with a link you’ll need to share with other parties who can sign this deploy and collect enough signing weight.'
        ),
      },
      expireAt: i18n.t('The deploy will expire at'),
      accountBalance: i18n.t(
        'Your account balance will update once the deploy is processed. It\n' +
          '      typically takes less than 1 minute, but the actual time depends on the\n' +
          '      level of the network activity. You can check the deploy status by\n' +
          '      clicking on'
      ),
    },
  },
  [NetworkMajorVersion.V2]: {
    common: {
      deploys: i18n.t('Transactions'),
      deploy: i18n.t('Transaction'),
      hash: i18n.t('Transaction Hash'),
      page: i18n.t('Transaction detail page'),
    },
    associatedKeys: {
      submission: i18n.t('Transaction submission'),
      submissionThresholds: i18n.t('Transaction submission threshold'),
      submissionThresholdsErrorTitle: i18n.t(
        'Transaction submission threshold error'
      ),
      submissionError: i18n.t(
        'The transaction submission threshold cannot be greater than the key management threshold'
      ),
      associatedKeysRemoval:
        "You are removing your key from the associated keys. It will lose it's signing rights after the transaction is executed.",
      tooltip: 'Signature weight required to submit a transaction',
    },
    homeScene: {
      past14days: i18n.t('TRANSACTIONS IN PAST 14 DAYS'),
      total: i18n.t('TOTAL TRANSACTIONS'),
      latestDeploys: i18n.t('Latest Transactions'),
      hash: i18n.t('Transaction Hash'),
      viewAll: i18n.t('View all Transactions'),
      chartDeploysNumber: i18n.t('Number of transactions'),
    },
    deployDetails: {
      pageTitle: i18n.t('Transaction Details'),
      tooltip: i18n.t('Transaction Details'),
      expireIn:
        "Transaction is waiting in the queue for the limited time. If the queue is too long and the deploy is not executed, the status will change to 'Expired' and the transaction will need to be done again. You can count expiration date as Timestamp + Expires in. The account is not charged for expired transactions.",
      actionTooltip: i18n.t(
        'Action the transaction caller intended to perform'
      ),
      resultTooltip: i18n.t(
        'Changes to the blockchain state this transaction resulted in'
      ),
      amountTooltip: i18n.t('Amount that is the subject of the transaction.'),
      paymentAmountTooltip: i18n.t(
        'Payment amount specifies the maximum amount that can be charged for executing the transaction. Part of the unused payment amount is refunded.'
      ),
      consumedGasTooltip: i18n.t(
        'The gas value, in CSPR, consumed to execute the transaction.'
      ),
      chargedAmountTooltip: i18n.t(
        'CSPR tokens spent for executing the transaction. Calculated as the payment amount minus the partial refund of unused gas.'
      ),
    },
    contracts: {
      tooltip: i18n.t(
        'Number of transactions that call the contract for the rest of the contracts'
      ),
    },
    faucet: {
      requests: i18n.t('transaction requests'),
    },
    analytics: {
      deploysOverTime: i18n.t('Daily transactions over time'),
      total: i18n.t('Total transactions'),
      graphKey: 'total_transactions',
    },
    associatedKeysLearnMore: {
      keyManagement: i18n.t(
        'The key management threshold specifies the weight required to modify\n' +
          '      the associated key settings, which is done via transaction that calls a\n' +
          '      corresponding smart contract.'
      ),

      weightInfo: i18n.t(
        'An associated key is an account with a weight assigned to\n' +
          '      it. Accounts listed as associated keys can sign and submit\n' +
          '      transactions on behalf of the master account as long as the\n' +
          '      cumulative weight of their signatures meets or exceeds the\n' +
          '      threshold required for the performed operation.'
      ),

      thresholdInfo: i18n.t(
        'The transaction submission threshold specifies the weight required to\n' +
          '      submit any other kind of a transaction.'
      ),
      video: i18n.t(
        '10 minutes tutorial on how to set up associated keys and multisig transactions.'
      ),
    },
    account: {
      transfers: i18n.t('Native Transfers'),
    },
    transfer: {
      error: i18n.t('Transaction was not found'),
      hash: i18n.t('The Transaction Hash for your transaction'),
    },
    multisig: {
      submitSignedTransactionFieldName: i18n.t('transaction'),
      tooltip:
        'Casper network allows accounts to authorise other accounts to\n' +
        '              submit transactions on their behalf. To do that, the authorised\n' +
        '              accounts need to collect enough signatures to satisfy the\n' +
        '              requirements imposed by the master account.',
      created: i18n.t('Transaction created'),
      submitted: i18n.t('Transaction submitted'),
      deployProcessed: i18n.t(
        'Your associated key settings will update once the transaction is\n' +
          '      processed. It typically takes less than 1 minute, but the actual\n' +
          '      time depends on the level of the network activity. You can check\n' +
          '      the transaction status by clicking on'
      ),
      copyLink: i18n.t(
        ' Please copy this link and share it with the other parties to\n' +
          '      add their signatures. The transaction will be automatically\n' +
          '      submitted, once enough weight is obtained.'
      ),
      expire: i18n.t('Transaction expires in 2 hours.'),
      cantSign: i18n.t('You can’t sign this transaction'),
      alreadySigned: i18n.t("You've already signed this transaction"),
      hasSignature: i18n.t(
        'The transaction you are attempting to sign already contains your signature.'
      ),
      notEnoughAlert: {
        title: i18n.t(
          'Your signature will not add enough weight to submit this transaction to the network.'
        ),
        description: i18n.t(
          'Once you sign it, you’ll be presented with a link you’ll need to share with other parties who can sign this transaction and collect enough signing weight.'
        ),
      },
      expireAt: i18n.t('The transaction will expire at'),
      accountBalance: i18n.t(
        'Your account balance will update once the transaction is processed. It\n' +
          '      typically takes less than 1 minute, but the actual time depends on the\n' +
          '      level of the network activity. You can check the transaction status by\n' +
          '      clicking on'
      ),
    },
  },
};
